
import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

export interface ErrorStateProps {
  title?: string;
  description?: string;
  error?: Error | string;
  onRetry?: () => void;
  className?: string;
  variant?: "default" | "compact" | "inline";
}

export function ErrorState({
  title = "Something went wrong",
  description = "There was an error processing your request",
  error,
  onRetry,
  className,
  variant = "default",
}: ErrorStateProps) {
  const errorMessage = error instanceof Error ? error.message : error;
  
  if (variant === "compact") {
    return (
      <Alert 
        variant="destructive" 
        className={cn("flex items-center", className)}
      >
        <AlertTriangle className="h-4 w-4 mr-2" />
        <div className="flex-1">
          <AlertTitle className="text-sm font-medium">{title}</AlertTitle>
          {description && <AlertDescription className="text-xs">{description}</AlertDescription>}
        </div>
        {onRetry && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onRetry}
            className="ml-2 h-7 px-2 text-xs"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Retry
          </Button>
        )}
      </Alert>
    );
  }
  
  if (variant === "inline") {
    return (
      <div className={cn("text-destructive flex items-center text-sm", className)}>
        <AlertTriangle className="h-4 w-4 mr-2" />
        <span>{title}</span>
        {onRetry && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onRetry}
            className="ml-2 h-6 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Retry
          </Button>
        )}
      </div>
    );
  }
  
  return (
    <div className={cn("rounded-md border border-destructive/50 p-4", className)}>
      <div className="flex items-center mb-3">
        <AlertTriangle className="h-5 w-5 text-destructive mr-2" />
        <h3 className="text-sm font-medium text-destructive">{title}</h3>
      </div>
      
      {description && (
        <div className="text-sm text-destructive/90 mb-3">
          {description}
        </div>
      )}
      
      {errorMessage && (
        <div className="text-xs bg-destructive/10 p-2 rounded mb-3 font-mono overflow-auto max-h-32">
          {errorMessage}
        </div>
      )}
      
      {onRetry && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onRetry}
          className="flex items-center text-sm"
        >
          <RefreshCw className="h-3 w-3 mr-2" />
          Try again
        </Button>
      )}
    </div>
  );
}
